import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Smile, Bold, Italic, ArrowLeft, Trash2 } from "lucide-react";
import { recomputeAndSaveDay, finalizePastDays } from "../lib/mood";

const DIARY_POSTS_KEY = "rl_diary_posts_v1";

function toDateKey(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function loadPostsAll() {
  try {
    const raw = localStorage.getItem(DIARY_POSTS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function savePostsAll(obj) {
  try { localStorage.setItem(DIARY_POSTS_KEY, JSON.stringify(obj)); } catch {}
}

export default function DiaryEdit() {
  const nav = useNavigate();
  const [search] = useSearchParams();
  const id = search.get("id");
  const dateFromQuery = search.get("date");
  const [date] = useState(() => {
    if (dateFromQuery) {
      const [y,m,d] = dateFromQuery.split("-").map(Number);
      if (y && m && d) return new Date(y, m-1, d);
    }
    return new Date();
  });
  const [title, setTitle] = useState("");
  const [type, setType] = useState("journaling");
  const [imageUrl, setImageUrl] = useState("");
  const [content, setContent] = useState("");
  const [mood, setMood] = useState(5);
  // date is not editable per requirement
  const [origTs, setOrigTs] = useState(Date.now());

  useEffect(() => {
    const all = loadPostsAll();
    const dk = dateFromQuery || toDateKey(date); // fixed date; cannot be changed
    const arr = all[dk] || [];
    const post = arr.find(p => p.id === id);
    if (post) {
      setTitle(post.title || "");
      setType(post.type || "journaling");
      setImageUrl(post.imageUrl || "");
      setContent(post.content || "");
      setMood(typeof post.mood === 'number' ? post.mood : 5);
      setOrigTs(post.ts || Date.now());
    }
  }, [id, dateFromQuery]);

  const applyWrap = (wrapStart, wrapEnd = wrapStart) => {
    const ta = document.getElementById("diary-edit-content");
    if (!ta) return;
    const start = ta.selectionStart ?? 0;
    const end = ta.selectionEnd ?? 0;
    const before = content.slice(0, start);
    const sel = content.slice(start, end);
    const after = content.slice(end);
    const next = before + wrapStart + (sel || "text") + wrapEnd + after;
    setContent(next);
    setTimeout(() => { try { ta.focus(); ta.setSelectionRange(before.length + wrapStart.length, before.length + wrapStart.length + (sel || "text").length); } catch {} }, 0);
  };

  const insertEmoji = (emoji) => setContent((t) => t + emoji);

  const onSave = () => {
    const all = loadPostsAll();
    const dk = dateFromQuery || toDateKey(date); // date locked; save back to same day
    const arr = all[dk] || [];
    // update in place by id
    const idx = arr.findIndex(p => p.id === id);
    const updated = { id, ts: origTs, title: title || "Journal", type, imageUrl, content, mood: Number(mood), editedAt: Date.now() };
    if (idx !== -1) arr[idx] = updated; else arr.push(updated);
    all[dk] = arr;
    savePostsAll(all);
    try {
      recomputeAndSaveDay(dk);
      finalizePastDays();
    } catch {}
    try { window.dispatchEvent(new Event("rl-diary-changed")); } catch {}
    nav(`/diary/day?date=${encodeURIComponent(dk)}`);
  };

  const onDelete = () => {
    const ok = window.confirm("Delete this post? This cannot be undone.");
    if (!ok) return;
    const all = loadPostsAll();
    const dk = dateFromQuery || toDateKey(date);
    const arr = all[dk] || [];
    const next = arr.filter(p => p.id !== id);
    all[dk] = next;
    savePostsAll(all);
    // Recompute mood for that day if not locked
    try {
      recomputeAndSaveDay(dk);
      finalizePastDays();
    } catch {}
    try { window.dispatchEvent(new Event("rl-diary-changed")); } catch {}
    nav(`/diary/day?date=${encodeURIComponent(dk)}`);
  };

  return (
    <div className="mx-auto max-w-3xl px-4 py-6 sm:py-10">
      <div className="mb-3 sm:flex sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={() => nav(`/diary/day?date=${encodeURIComponent(dateFromQuery || toDateKey(date))}`)} data-testid="diary-edit-back-button"><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>
          <div className="text-xl font-semibold">Edit post</div>
        </div>
        <div className="mt-2 sm:mt-0 grid grid-cols-2 gap-2 w-full sm:w-auto">
          <Button variant="destructive" onClick={onDelete} data-testid="diary-edit-delete-button"><Trash2 className="h-4 w-4 mr-1" /> Delete</Button>
          <Button className="btn-primary" onClick={onSave} data-testid="diary-edit-save-button">Save changes</Button>
        </div>
      </div>

      <div className="grid gap-4">
        <Input value={title} onChange={(e)=>setTitle(e.target.value)} placeholder="Title" />
        <div className="grid sm:grid-cols-3 gap-3">
          <div>
            <label className="text-xs text-slate-500">Post type</label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger className="mt-1"><SelectValue placeholder="Select type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="journaling">Journaling</SelectItem>
                <SelectItem value="trigger">Trigger</SelectItem>
                <SelectItem value="relapse">Relapse</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-slate-500">Image URL (optional)</label>
            <Input value={imageUrl} onChange={(e)=>setImageUrl(e.target.value)} placeholder="https://..." className="mt-1" />
          </div>
          <div>
            <label className="text-xs text-slate-500">Mood (1–10)</label>
            <div className="mt-2 flex items-center gap-3">
              <input type="range" min={1} max={10} value={mood} onChange={(e)=>setMood(parseInt(e.target.value))} className="w-full h-2 rounded-lg appearance-none cursor-pointer" style={{ background: "linear-gradient(90deg, #FF0000 0%, #FFD93D 50%, #00C853 100%)" }} />
              <span className="text-sm font-semibold">{mood}</span>
            </div>
          </div>
        </div>

        <div>
          <div className="flex items-center gap-2 mb-2">
            <Button type="button" variant="outline" size="sm" aria-label="Bold" onClick={()=>applyWrap("**","**")}><Bold className="h-4 w-4" /></Button>
            <Button type="button" variant="outline" size="sm" aria-label="Italic" onClick={()=>applyWrap("*","*")}><Italic className="h-4 w-4" /></Button>
            <Button type="button" variant="outline" size="sm" aria-label="Underline" onClick={()=>applyWrap("__","__")}><span className="text-xs font-semibold">U</span></Button>
            <Popover>
              <PopoverTrigger asChild>
                <Button type="button" variant="outline" size="sm" aria-label="Emoji"><Smile className="h-4 w-4" /></Button>
              </PopoverTrigger>
              <PopoverContent className="grid grid-cols-8 gap-1 p-2 w-[220px]">
                {"😀😁😂🤣😊😍🤔😇😎😭😡😴🙏👍👏🔥❤️💪🌟✨🎉".split("").filter(Boolean).map((e,i)=> (
                  <button key={i} className="text-xl" onClick={()=>setContent((t)=>t+e)}>{e}</button>
                ))}
              </PopoverContent>
            </Popover>
          </div>
          <Textarea id="diary-edit-content" value={content} onChange={(e)=>setContent(e.target.value)} placeholder="Edit your post..." className="min-h-[260px] md:min-h-[260px]" />
        </div>
      </div>
    </div>
  );
}